import React, { Suspense } from "react";
import { Route, Routes, BrowserRouter } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import SuperLayout from "../app/layout/SuperLayout";
import AppLayout from "../app/layout/AppLayout";
import ProtectedRoute from "./ProtectedRoute";
import NotFound from "../app/pages/NotFound";
import LoggedInRoutes from "./LoggedInRoutes";

const Login = React.lazy(() => import(/* webpackPrefetch: true */ "../auth/pages/login/Login"));
const Register = React.lazy(() => import(/* webpackPrefetch: true */ "../auth/pages/register/Register"));
const TempPassword = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../auth/pages/tempPassword/TempPassword")
);
const Home = React.lazy(() => import(/* webpackPrefetch: true */ "../landing_page/pages/home/Index"));
const Ship = React.lazy(() => import(/* webpackPrefetch: true */ "../customer/pages/ship/Ship"));
const Track = React.lazy(() => import(/* webpackPrefetch: true */ "../customer/pages/track/Track"));
const Admin = React.lazy(() => import(/* webpackPrefetch: true */ "../super/pages/admin/Admin"));
const ServiceProvider = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/service_provider/ServiceProvider")
);
const Customer = React.lazy(() => import(/* webpackPrefetch: true */ "../admin/pages/customer/Customer"));
const OfficeNotices = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/office_notice/OfficeNotices")
);
const MyShipmentRequests = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../customer/pages/ship/MyShipmentRequests")
);
const MyShipments = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../customer/pages/ship/MyShipments")
);
const OfficeNoticesDetailPage = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/office_notice/detail/OfficeNoticesDetailPage")
);
const Settings = React.lazy(() => import(/* webpackPrefetch: true */ "../admin/pages/settings/Settings"));
const CustomerNotice = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/customer_notice/CustomerNotice")
);
const CustomerNoticesDetailPage = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/customer_notice/detail/CustomerNoticesDetailPage")
);
const CustomerDetailPage = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/customer/detail/CustomerDetailPage")
);
const MyShipmentDetailPage = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../customer/pages/ship/detail/MyShipmentDetailPage")
);
const AdminDashboard = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/dashboard/AdminDashboard")
);
const Dashboard = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../customer/pages/dashboard/Dashboard")
);
const ServiceProviderDetailPage = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/service_provider/detail/ServiceProviderDetailPage").then(
    (m) => ({ default: m.ServiceProviderDetailPage })
  )
);
const Customshipment = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/custom_shipment/Customshipment").then((m) => ({
    default: m.Customshipment,
  }))
);
const AddShipmentLite = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/nepal_lite/AddShipmentLite")
);
const ShipmentTable = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/nepal_lite/Shipment/ShipmentTable")
);
const EditShipmentLite = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/nepal_lite/EditShipmentLite")
);
const UpdateConfirmShipmentLite = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/nepal_lite/UpdateConfirmShipmentLite")
);
const UpdateShipmentBillingForm = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/components/form/nepal_lite/update_shipment/UpdateShipmentBillingForm")
);
const PickUp = React.lazy(() => import(/* webpackPrefetch: true */ "../admin/pages/pickup/PickUp"));
const PickupDetail = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/pickup/detail/PickupDetail").then((m) => ({
    default: m.PickupDetail,
  }))
);
const Shipments = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/shipment-final/Shipments")
);
const ShipmentDetail = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/shipment-final/detail/ShipmentDetail")
);
const Reports = React.lazy(() => import(/* webpackPrefetch: true */ "../admin/pages/reports/Reports"));
const About = React.lazy(() => import(/* webpackPrefetch: true */ "../landing_page/pages/about/About"));
const Services = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../landing_page/pages/services/Services")
);
const Contact = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../landing_page/pages/contact/Contact")
);
const BilledShipments = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/billed_shipments/BilledShipments")
);
const BilledShipmentDetail = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/billed_shipments/detail/BilledShipmentDetail")
);
const MyPickup = React.lazy(() => import(/* webpackPrefetch: true */ "../customer/pages/pickup/MyPickup"));
const ZoneDetail = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/service_provider/detail/zone/ZoneDetail")
);
const Payment = React.lazy(() => import(/* webpackPrefetch: true */ "../admin/pages/payments/Payment"));
const Payments = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../customer/pages/payments/Payments")
);
const NepalExInfo = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../customer/pages/nepalex-info/NepalExInfo")
);
const MyBillDetails = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../customer/pages/payments/bill-details/MyBillDetails")
);
const MasterAWB = React.lazy(() =>
  import(/* webpackPrefetch: true */ "../admin/pages/nepal_lite/Shipment/MasterAWB/MasterAWB")
);

const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <CircularProgress />
  </Box>
);

const AppRoute = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" exact element={<AppLayout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="services" element={<Services />} />
            <Route path="contact" element={<Contact />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/signup/">
            <Route path="super" element={<Register superSignUp={true} />} />
          </Route>

          <Route path="/" element={<ProtectedRoute redirectTo="/login" />}>
            <Route element={<SuperLayout />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="track" element={<Track />} />
              <Route path="shipping-request" element={<Ship />} />
              <Route
                path="my-shipment-requests"
                element={<MyShipmentRequests />}
              />
              <Route path="my-shipments" element={<MyShipments />} />
              <Route path="my-pickups" element={<MyPickup />} />
              <Route path="payments" element={<Payments />} />
              <Route path="nepalex-info" element={<NepalExInfo />} />
              <Route path="settings" element={<Settings />} />

              <Route
                path="my-shipments/:id"
                element={<MyShipmentDetailPage />}
              />
              <Route path="bill/:id" element={<MyBillDetails />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute redirectTo="/login" />}>
            <Route path="temp-password" element={<TempPassword />} />
            <Route path="/super" element={<SuperLayout />}>
              <Route
                element={<LoggedInRoutes redirectTo="/404" isRole="SysAdmin" />}
              >
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="settings" element={<Settings />} />
                <Route path="admin" element={<Admin />} />
                <Route path="pickup" element={<PickUp />} />
                <Route path="service-provider" element={<ServiceProvider />} />
                <Route path="customer" element={<Customer />} />
                <Route path="office-notices" element={<OfficeNotices />} />
                <Route path="customer-notices" element={<CustomerNotice />} />
                <Route path="custom-shipment" element={<Customshipment />} />
                <Route
                  path="add-shipment-lite"
                  element={<AddShipmentLite />}
                />
                <Route path="master-awb" element={<MasterAWB />} />
                <Route path="shipment-lite" element={<ShipmentTable />} />
                <Route path="payments" element={<Payment />} />
                <Route
                  path="final-shipments"
                  element={<Shipments billed={false} />}
                />
                <Route
                  path="billed-shipments"
                  element={<Shipments billed={true} />}
                />
                <Route path="bill" element={<BilledShipments />} />
                <Route path="reports" element={<Reports />} />
                <Route
                  path="office-notices/:id"
                  element={<OfficeNoticesDetailPage />}
                />
                <Route
                  path="service-provider/:id"
                  element={<ServiceProviderDetailPage />}
                />
                <Route
                  path="service-provider/:id/:zone"
                  element={<ZoneDetail />}
                />
                <Route
                  path="customer-notices/:id"
                  element={<CustomerNoticesDetailPage />}
                />
                <Route path="customer/:id" element={<CustomerDetailPage />} />
                <Route
                  path="update/shipment-billing/:id"
                  element={<UpdateShipmentBillingForm />}
                />
                <Route path="pickup/:id" element={<PickupDetail />} />
                <Route path="shipment/:id" element={<ShipmentDetail />} />

                <Route
                  path="edit-shipment-lite/:id"
                  element={<EditShipmentLite />}
                />
                <Route
                  path="edit-confirmshipment-lite/:id"
                  element={<UpdateConfirmShipmentLite />}
                />
                <Route path="bill/:id" element={<BilledShipmentDetail />} />
              </Route>
            </Route>
            <Route path="/admin" element={<SuperLayout />}>
              <Route
                element={<LoggedInRoutes redirectTo="/404" isRole="Admin" />}
              >
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="admin-list" element={<Admin />} />
                <Route path="service-provider" element={<ServiceProvider />} />
                <Route path="customer" element={<Customer />} />
                <Route path="office-notices" element={<OfficeNotices />} />
                <Route path="settings" element={<Settings />} />
                <Route path="customer-notices" element={<CustomerNotice />} />
                <Route path="custom-shipment" element={<Customshipment />} />
                <Route path="pickup" element={<PickUp />} />
                <Route
                  path="add-shipment-lite"
                  element={<AddShipmentLite />}
                />
                <Route path="shipment-lite" element={<ShipmentTable />} />
                <Route path="payments" element={<Payment />} />
                <Route
                  path="final-shipments"
                  element={<Shipments billed="All" />}
                />
                <Route path="bill" element={<BilledShipments />} />
                <Route path="reports" element={<Reports />} />

                <Route
                  path="office-notices/:id"
                  element={<OfficeNoticesDetailPage />}
                />
                <Route
                  path="customer-notices/:id"
                  element={<CustomerNoticesDetailPage />}
                />
                <Route path="customer/:id" element={<CustomerDetailPage />} />
                <Route
                  path="service-provider/:id"
                  element={<ServiceProviderDetailPage />}
                />
                <Route
                  path="service-provider/:id/:zone"
                  element={<ZoneDetail />}
                />
                <Route path="pickup/:id" element={<PickupDetail />} />
                <Route path="shipment/:id" element={<ShipmentDetail />} />

                <Route
                  path="edit-shipment-lite/:id"
                  element={<EditShipmentLite />}
                />
                <Route
                  path="edit-confirmshipment-lite/:id"
                  element={<UpdateConfirmShipmentLite />}
                />
                <Route path="bill/:id" element={<BilledShipmentDetail />} />
              </Route>
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRoute;
